import { AnimatePresence, motion } from 'framer-motion';
import { useLocale } from 'next-intl';
import type { HeaderProps as HeaderDataProps } from '@/ui/components/Header/header-types';
import useHeaderSummaryVisibility from '@/core/hooks/use-header-summary-visibility';
import useTranslations from '@/core/hooks/use-translations';
import { usePathname } from '@/core/i18n/routing';
import AccountLink from '@/ui/components/AccountLink/AccountLink';
import Button from '@/ui/components/Button/Button';
import CartLink from '@/ui/components/CartLink/CartLink';
import LanguageChanger from '@/ui/components/LanguageChanger/LanguageChanger';
import LogoLink from '@/ui/components/LogoLink/LogoLink';
import MobileMenu from '@/ui/components/MobileMenu/MobileMenu';
import SkipLink from '@/ui/components/SkipLink/SkipLink';
import { useGA4 } from '@/ui/context/ga4';
import useBrowseOnlyBookingAccountAccess from '@/ui/hooks/use-browse-only-booking-account-access';
import cn from '@/ui/utils/cn/cn';
import HeaderSummary from './HeaderSummary';

import $ from './Header.module.scss';

type HeaderProps = {
  /** Header data from Uniform composition. Navigation items render only when present. */
  headerData?: HeaderDataProps | null;
};

const Header = ({ headerData }: HeaderProps) => {
  const locale = useLocale();
  const t = useTranslations();
  const { trackMenuNavigation } = useGA4();
  const pathname = usePathname();
  const { showHeaderAccountLink, showHeaderCart, accountEntryHref } =
    useBrowseOnlyBookingAccountAccess();

  const isSummaryVisible = useHeaderSummaryVisibility();

  const navigationItems =
    Array.isArray(headerData?.navigationItems) &&
    headerData.navigationItems.length > 0
      ? headerData.navigationItems
      : [];

  const isActive = (path: string) => path === pathname;

  const motionVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.35 },
  };

  return (
    <header className={$.container} role="banner">
      <SkipLink targetId="main">{t('web_general_header_skipLink')}</SkipLink>

      <nav
        className={cn($.navigation, isSummaryVisible && $.navigationSummary)}
      >
        <div
          className={
            isSummaryVisible
              ? cn($.mobileMenuWrapper, $.mobileHidden, $.mobileZeroWidth)
              : $.mobileMenuWrapper
          }
        >
          <MobileMenu
            navigationItems={navigationItems}
            menuOpenLabel={headerData?.menuOpenLabel}
            menuCloseLabel={headerData?.menuCloseLabel}
          />
        </div>

        <div
          className={
            isSummaryVisible ? cn($.logoWrapper, $.mobileHidden) : $.logoWrapper
          }
        >
          <LogoLink renderOn="header" />
        </div>

        <AnimatePresence mode="wait">
          {isSummaryVisible ? (
            <motion.div key="summary" {...motionVariants} className={$.summary}>
              <HeaderSummary />
            </motion.div>
          ) : (
            <motion.div key="list" {...motionVariants}>
              <ul className={$.list}>
                {navigationItems.map(item => (
                  <li
                    key={item.path}
                    className={cn(
                      $.listItem,
                      $.desktop,
                      isActive(item.path) && $.active,
                    )}
                    data-testid={`header-navigation-item-${item.label?.replace(' ', '-')}`}
                  >
                    <Button
                      href={item.path}
                      variant="tertiary"
                      className={$.listItemButton}
                      onClick={() => {
                        trackMenuNavigation({
                          menu: 'header',
                          menu_path: item.path,
                          menu_text: item.trackTitle ?? item.label,
                          menu_click: 'text',
                        });
                      }}
                    >
                      {item.label}
                    </Button>
                  </li>
                ))}
                {showHeaderAccountLink && (
                  <li
                    className={cn(
                      $.listItem,
                      $.listItemAccount,
                      $.desktop,
                      pathname.startsWith('/account') && $.active,
                    )}
                    data-testid="header-account"
                  >
                    <AccountLink href={accountEntryHref} />
                  </li>
                )}
                {showHeaderCart && (
                  <li
                    className={cn(
                      $.listItem,
                      $.listItemCart,
                      pathname.startsWith('/cart') && $.active,
                    )}
                    data-testid="header-cart"
                  >
                    <CartLink />
                  </li>
                )}

                <li
                  className={cn($.listItem, $.languageChanger)}
                  data-testid="header-language-changer"
                >
                  <LanguageChanger locale={locale} />
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
